import React from 'react';
import { Button, Chip, IconButton, Tooltip } from '@mui/material';
import ActionIconTooltip from '../ui/ActionIconTooltip';
import { QrCode2 as QrCodeIcon } from '@mui/icons-material';
import { ASAAS_CHARGE_STATUS_META, formatBRL, type AsaasChargeStatus } from '../../services/asaasService';

/** Espelho da cobrança gravado pelo backend em metadata.asaas_charge da conta a receber. */
export function receivableAsaasCharge(rec: Record<string, any> | null | undefined): {
  status: AsaasChargeStatus;
  value?: number;
  sent_whatsapp_at?: string | null;
  alert?: string;
} | null {
  const mirror = rec?.metadata?.asaas_charge;
  if (!mirror || typeof mirror !== 'object' || !mirror.status) return null;
  return mirror;
}

export function receivableHasOpenAsaasCharge(rec: Record<string, any> | null | undefined): boolean {
  const st = receivableAsaasCharge(rec)?.status;
  return st === 'pending' || st === 'overdue';
}

interface ButtonProps {
  receivable: Record<string, any>;
  onClick: () => void;
  variant?: 'icon' | 'button';
}

export const AsaasReceivableChargeButton: React.FC<ButtonProps> = ({ receivable, onClick, variant = 'icon' }) => {
  const open = receivableHasOpenAsaasCharge(receivable);
  const mirror = receivableAsaasCharge(receivable);
  const title = open
    ? `Cobrança Asaas em aberto${mirror?.value ? ` (${formatBRL(mirror.value)})` : ''} — ver QR Code, link e reenviar`
    : 'Cobrar via Asaas: link, PIX QR Code e envio no WhatsApp';

  if (variant === 'button') {
    return (
      <Button
        size="small"
        variant={open ? 'outlined' : 'contained'}
        color={open ? 'warning' : 'primary'}
        startIcon={<QrCodeIcon fontSize="small" />}
        onClick={(e) => { e.stopPropagation(); onClick(); }}
      >
        {open ? 'Cobrança Asaas em aberto' : 'Cobrar via Asaas'}
      </Button>
    );
  }

  return (
    <ActionIconTooltip title={title}>
      <IconButton
        size="small"
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        sx={{ color: open ? '#d97706' : '#0284c7' }}
        aria-label="Cobrar via Asaas"
      >
        <QrCodeIcon fontSize="small" />
      </IconButton>
    </ActionIconTooltip>
  );
};

/** Chip discreto ao lado do status da conta: só aparece com cobrança Asaas relevante. */
export const AsaasReceivableChargeChip: React.FC<{ receivable: Record<string, any> }> = ({ receivable }) => {
  const mirror = receivableAsaasCharge(receivable);
  if (!mirror) return null;
  if (mirror.status === 'cancelled') return null;
  const meta = ASAAS_CHARGE_STATUS_META[mirror.status];
  if (!meta) return null;
  const label = mirror.status === 'paid' ? 'Pago via Asaas' : `Asaas · ${meta.label.toLowerCase()}`;
  return (
    <Tooltip title={mirror.alert || (mirror.sent_whatsapp_at ? 'Cobrança enviada no WhatsApp' : 'Cobrança gerada na Asaas')}>
      <Chip
        label={label}
        size="small"
        color={mirror.alert ? 'error' : meta.color}
        variant="outlined"
        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, ml: 0.5 }}
      />
    </Tooltip>
  );
};

export default AsaasReceivableChargeButton;
